import { union, featureCollection, polygon } from '@turf/turf'

const DEG_TO_RAD = Math.PI / 180
const METERS_PER_DEG = 111_320
const MIN_HALF_WIDTH_M = 3

/**
 * Build an uncertainty corridor as a single buffer that hugs the centerline.
 *
 * Each segment becomes a trapezoid offset left/right by the measured GPS scatter
 * at its endpoints; all trapezoids are then **dissolved into one polygon** so the
 * corridor reads as a continuous band, not a pile of overlapping translucent
 * quads. Where the track doubles back (out-and-back), the overlapping return-trip
 * buffer merges into the same shape instead of stacking, and any genuinely
 * enclosed gap becomes a hole.
 *
 * Replaces the old concave-hull-of-all-points approach, which wrapped the whole
 * scatter cloud (and filled the interior of every loop) instead of the line.
 *
 * Returns MultiPolygon coordinates (array of polygons, each `[outerRing, ...holes]`).
 *
 * @param coords      the centerline (display) points, [lng, lat]
 * @param halfWidths  per-point corridor half-width in meters (e.g. displayScatter)
 */
export function computeUncertaintyCorridor(
  coords: GeoJSON.Position[],
  halfWidths: number[]
): GeoJSON.Position[][][] {
  if (coords.length < 2) return []

  // Drop consecutive coincident points (e.g. collapsed rests) so every segment
  // has a defined direction; carry the larger half-width forward.
  const pts: GeoJSON.Position[] = [coords[0]]
  const hw: number[] = [Math.max(halfWidths[0] ?? MIN_HALF_WIDTH_M, MIN_HALF_WIDTH_M)]
  for (let i = 1; i < coords.length; i++) {
    const [px, py] = pts[pts.length - 1]
    const ex =
      (coords[i][0] - px) * METERS_PER_DEG * Math.cos((coords[i][1] + py) * 0.5 * DEG_TO_RAD)
    const ny = (coords[i][1] - py) * METERS_PER_DEG
    const w = Math.max(halfWidths[i] ?? MIN_HALF_WIDTH_M, MIN_HALF_WIDTH_M)
    if (Math.hypot(ex, ny) < 0.5) {
      hw[hw.length - 1] = Math.max(hw[hw.length - 1], w)
      continue
    }
    pts.push(coords[i])
    hw.push(w)
  }
  if (pts.length < 2) return []

  // Unit left-normal (meters frame) of each segment i → i+1.
  const segNx: number[] = []
  const segNy: number[] = []
  for (let i = 0; i < pts.length - 1; i++) {
    const midLat = (pts[i][1] + pts[i + 1][1]) * 0.5
    const ex = (pts[i + 1][0] - pts[i][0]) * METERS_PER_DEG * Math.cos(midLat * DEG_TO_RAD)
    const ny = (pts[i + 1][1] - pts[i][1]) * METERS_PER_DEG
    const len = Math.hypot(ex, ny) || 1
    segNx.push(-ny / len)
    segNy.push(ex / len)
  }

  // Vertex normal = unit bisector of adjacent segment normals (mitered join), so
  // consecutive quads share endpoints exactly and the union has no V-notches.
  const vNx: number[] = []
  const vNy: number[] = []
  for (let i = 0; i < pts.length; i++) {
    const ax = i > 0 ? segNx[i - 1] : segNx[0]
    const ay = i > 0 ? segNy[i - 1] : segNy[0]
    const bx = i < pts.length - 1 ? segNx[i] : segNx[segNx.length - 1]
    const by = i < pts.length - 1 ? segNy[i] : segNy[segNy.length - 1]
    let sx = ax + bx
    let sy = ay + by
    const len = Math.hypot(sx, sy)
    if (len < 1e-6) {
      sx = bx
      sy = by
    } else {
      sx /= len
      sy /= len
    }
    vNx.push(sx)
    vNy.push(sy)
  }

  const offset = (lng: number, lat: number, eastM: number, northM: number): GeoJSON.Position => [
    lng + eastM / (METERS_PER_DEG * Math.cos(lat * DEG_TO_RAD)),
    lat + northM / METERS_PER_DEG,
  ]
  const left = (i: number): GeoJSON.Position =>
    offset(pts[i][0], pts[i][1], vNx[i] * hw[i], vNy[i] * hw[i])
  const right = (i: number): GeoJSON.Position =>
    offset(pts[i][0], pts[i][1], -vNx[i] * hw[i], -vNy[i] * hw[i])

  const quads: GeoJSON.Position[][][] = []
  for (let i = 0; i < pts.length - 1; i++) {
    quads.push([[left(i), left(i + 1), right(i + 1), right(i), left(i)]])
  }

  // Round joins: a bevel undercovers the outer corner at a sharp turn, leaving a
  // notch. Drop a disk (radius = half-width) at each sharp vertex and at both end
  // caps so the union rounds those corners off instead.
  const disk = (i: number): GeoJSON.Position[][] => {
    const ring: GeoJSON.Position[] = []
    const K = 12
    for (let j = 0; j < K; j++) {
      const a = (2 * Math.PI * j) / K
      ring.push(offset(pts[i][0], pts[i][1], hw[i] * Math.cos(a), hw[i] * Math.sin(a)))
    }
    ring.push(ring[0]) // close exactly (avoid float drift at j=K)
    return [ring]
  }
  if (pts.length >= 2) {
    quads.push(disk(0), disk(pts.length - 1)) // rounded end caps
  }
  for (let i = 1; i < pts.length - 1; i++) {
    // dot of consecutive segment normals = cos(turn angle); < cos(12°) ⇒ sharp
    const dotN = segNx[i - 1] * segNx[i] + segNy[i - 1] * segNy[i]
    if (dotN < 0.978) quads.push(disk(i))
  }

  if (quads.length === 0) return []
  if (quads.length === 1) return quads

  // Dissolve all trapezoids into one continuous band (with holes where the path
  // encloses area). Fall back to the raw quads if the union ever fails.
  try {
    const merged = union(featureCollection(quads.map(q => polygon(q))))
    if (!merged) return quads
    return merged.geometry.type === 'Polygon'
      ? [merged.geometry.coordinates]
      : merged.geometry.coordinates
  } catch {
    return quads
  }
}
